<template>
  <div class="products-portfolio">
    <div class="container">
      <div
        class="products-portfolio__content"
        :style="{ '--slides-visible': slidesToShow }">
        <!-- title & link area -->
        <div class="products-portfolio__header">
          <slot name="title"></slot>
          <slot v-if="!onMobile" name="link"></slot>
        </div>

        <!-- filters (tabs) -->
        <div
          v-if="!loading && checkObjectSize(portfolioCategories)"
          class="slider slider-filters products-portfolio__filters slider--no-auto-height"
          :class="{
            'slider--inactive': portfolioCategories.length <= filtersToShow,
          }">
          <div class="slider__viewport slider-filters__viewport">
            <div
              class="slider__container slider-filters__container"
              :style="{ '--filters-visible': slidesToShow }">
              <div
                v-for="filter in portfolioCategories"
                class="slider-filters-slide slider__slide"
                :class="{
                  'products-portfolio__filter--active':
                    activeCategory.id === filter.id,
                }"
                :key="filter.id">
                <button
                  class="products-portfolio__filter"
                  @click="setActiveCategory(filter.id)">
                  <!-- small icon -->
                  <!-- (optional, but we do nothing about it if empty, for now) -->
                  <div class="products-portfolio__image-wrapper">
                    <div class="hexagon-image__wrapper">
                      <div class="products-portfolio__image hexagon-image">
                        <responsive-image
                          :images="filter.icon"
                          v-if="filter.icon" />
                      </div>
                    </div>
                  </div>

                  <!-- category label but only on desktop -->
                  <span
                    v-if="!onMobile"
                    class="products-portfolio__filter-label">
                    {{ filter.label }}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- sliders -->
        <loading v-if="loading"></loading>
        <div v-else class="products-portfolio__results">
          <!-- moves from under the hexagon to above the results -->
          <!-- on mobile -->
          <span v-if="onMobile" class="products-portfolio__filter-label">
            {{ activeCategory.label }}
          </span>

          <div
            class="slider slider-portfolio slider--visible-slides"
            :class="{ 'slider--inactive': !sliderActive }">
            <div class="slider__viewport slider-portfolio__viewport">
              <div class="slider__container slider-portfolio__container">
                <template v-if="activeCategory && activeCategory.items.length">
                  <!-- @todo url can be empty, what then? -->
                  <a
                    v-for="product in activeCategory.items"
                    :href="product.url"
                    class="products-portfolio__slide slider__slide"
                    :key="product.id">
                    <!-- product image - can be empty, we nothing about it yet -->
                    <div class="products-portfolio__result product-teaser">
                      <div
                        v-if="product.product_image"
                        class="products-portfolio__result-image product-teaser__image">
                        <responsive-image :images="product.product_image" />
                      </div>
                      <h4>{{ product.label }}</h4>
                    </div>
                  </a>
                </template>
                <div v-else class="products-portfolio__empty text-formatted">
                  <p>{{ t.productsPortfolio.noResults }}</p>
                </div>
              </div>
            </div>

            <!-- scrollbar -->
            <div v-if="sliderActive && !onMobile" class="slider__footer">
              <div class="slider-portfolio__scroll slider__scroll">
                <!-- @todo prettier hates next line - vue/first-attribute-linebreak wants it -->
                <div
                  class="slider-portfolio__scroll-bar slider__scroll-bar"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="product-portfolio__more">
          <a
            :href="activeCategory.url"
            v-if="checkObjectSize(activeCategory) && activeCategory.url"
            class="button">
            {{
              t.productsPortfolio.linkMore.replace(
                '@category_name',
                activeCategory.label,
              )
            }}
          </a>

          <!-- to product catalog link (mobile only) -->
          <slot v-if="onMobile" name="link"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// components
import Loading from './Loading';
import ResponsiveImage from './ResponsiveImage';
// utils
import buildSlider from '../elements/slider';
import isMobile from '../utils/is-mobile';
import translationStrings from '../utils/translation-strings';
import { getPortfolioData } from '../utils/api';

export default {
  name: 'products-portfolio',
  components: {
    Loading,
    ResponsiveImage,
  },
  props: {
    lang: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      activeCategory: {},
      filtersToShow: 6,
      loading: true,
      onMobile: isMobile(),
      productData: [],
      sliderActive: false,
      sliderFilters: null,
      sliderResults: null,
      slidesToShow: 5,
      t: translationStrings,
    };
  },
  computed: {
    portfolioCategories() {
      // get just the data for the tabs
      return this.productData.map(({ id, label, icon }) => ({
        id,
        label,
        icon,
      }));
    },
    sliderSharedOptions() {
      return {
        containScroll: 'trimSnaps',
        slidesToScroll: 1,
        watchDrag: !this.onMobile,
      };
    },
  },
  watch: {
    activeCategory(newActiveCategory) {
      this.sliderActive =
        'items' in this.activeCategory
          ? newActiveCategory?.items.length > this.slidesToShow
          : false;
    },
  },
  mounted() {
    this.fetchData();

    window.addEventListener('resize', () => {
      // re-check for mobile
      this.onMobile = isMobile();

      if (this.sliderFilters != null && this.onMobile) {
        this.sliderFilters.reInit({
          active: true,
        });
      }
    });
  },
  updated() {
    // just to really make sure elements are ready
    if (!this.loading) {
      // init the results slider
      this.buildSliderPortfolio();

      if (this.sliderFilters == null) {
        this.buildSliderFilters();
      }
    }
  },
  methods: {
    async fetchData() {
      try {
        const { data } = await getPortfolioData(this.lang);
        this.productData = data.items;
      } catch ({ response }) {
        // allow console log so we can see at least some error message
        // eslint-disable-next-line no-console
        console.log('❗ Something went wrong.', response);
      } finally {
        // set first item as active
        this.activeCategory = this.productData[0] ?? {};
        if ('items' in this.activeCategory) {
          // disable loading
          this.loading = false;
        }
      }
    },
    buildSliderPortfolio() {
      // destroy the previous one before starting a new one
      this.sliderResults?.destroy();

      this.sliderResults = buildSlider('.slider-portfolio', {
        ...this.sliderSharedOptions,
        active: !this.onMobile,
        align: 'start',
        inViewThreshold: 0.2,
        watchDrag: this.sliderActive,
      });
    },
    buildSliderFilters() {
      this.sliderFilters = buildSlider('.slider-filters', {
        ...this.sliderSharedOptions,
        align: 'center',
        breakpoints: {
          '(min-width: 0px)': {
            active: true,
            watchDrag:
              this.isMobile ||
              this.slidesToShow > this.portfolioCategories.length,
          },
          '(min-width: 1181px)': {
            active: false,
            watchDrag: false,
          },
        },
        inViewThreshold: 0.99,
      });
    },
    setActiveCategory(activeCategoryId) {
      this.activeCategory = this.productData.find(
        (d) => d.id === activeCategoryId,
      );
      const currentSlidePosition = this.productData.indexOf(
        this.activeCategory,
      );

      this.sliderResults?.scrollTo(currentSlidePosition);
      this.sliderFilters?.scrollTo(currentSlidePosition);
    },
    checkObjectSize(object) {
      return Object.keys(object).length > 0;
    },
  },
};
</script>
